import { HttpClientModule } from "@angular/common/http";
import { ErrorHandler, Injectable, NgModule } from "@angular/core";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router } from "@angular/router";
import { JwtModule, JWT_OPTIONS } from "@auth0/angular-jwt";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { NgxsEmitPluginModule } from "@ngxs-labs/emitter";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsFormPluginModule } from "@ngxs/form-plugin";
import { NgxsLoggerPluginModule } from "@ngxs/logger-plugin";
import { NgxsRouterPluginModule } from "@ngxs/router-plugin";
import { NgxsStoragePluginModule } from "@ngxs/storage-plugin";
import { NgxsModule, Store } from "@ngxs/store";
import * as Sentry from "@sentry/browser";
import { FroalaEditorModule, FroalaViewModule } from "angular-froala-wysiwyg";
// Import all Froala Editor plugins.
import "froala-editor/js/plugins.pkgd.min.js";
// Import a single Froala Editor plugin.
import "froala-editor/js/plugins/align.min.js";
import { CookieService } from "ngx-cookie-service";
import { environment } from "src/environments/environment";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app.module.routing";
import { CompanyRegisterFormState } from "./auth/state/company-register-form.state";
import { ngxsConfig } from "./ngxs.config";
import { NotFoundComponent } from "./not-found/not-found.component";
import { PortalAuthState } from "./portal/shared/state/portal-auth.state";
import { SignInFormState } from "./portal/sign-in/state/sign-in-form.state";
import { AuthV1Service } from "./shared/service/auth-v1.service";
import {
    AppDateAdapter,
    APP_DATE_FORMATS,
    LuxonDateFormatter
} from "./shared/service/luxon-date-formatter";
import { StoredAuthSetAction } from "./shared/state/stored-auth.actions";
import { StoredAuthState } from "./shared/state/stored-auth.state";
import { StoredPaginationState } from "./shared/state/stored-pagination.state";
import { RequestDemoFormState } from "./website/state/request-demo-form.state";

Sentry.init({
	dsn: "https://4919fb7dd8714c8cb259cc6bcbd91c76@sentry.io/70317",
	beforeSend(event, hint) {
		/* tslint:disable:no-string-literal only-arrow-functions */
		/* tslint:enable:no-string-literal only-arrow-functions */
		const isNonErrorException =
			(event.exception &&
				event.exception.values &&
				event.exception.values[0] &&
				event.exception.values[0].value &&
				event.exception.values[0].value.startsWith(
					"Non-Error exception captured"
				)) ||
			(hint &&
				hint.originalException &&
				hint.originalException["message"] &&
				hint.originalException["message"].startsWith(
					"Non-Error exception captured"
				));
		const NS_ERROR_NOT_INITIALIZED =
			(event.exception &&
				event.exception.values &&
				event.exception.values[0] &&
				event.exception.values[0].value &&
				event.exception.values[0].value.startsWith("NS_ERROR_NOT_INITIALIZED")) ||
			(hint &&
				hint.originalException &&
				hint.originalException["message"] &&
				hint.originalException["message"].startsWith("NS_ERROR_NOT_INITIALIZED"));

		if (isNonErrorException || NS_ERROR_NOT_INITIALIZED) {
			// We want to ignore those kind of errors
			return null;
		}
		return event;
	},
});

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
	constructor() {}
	handleError(error) {
		const chunkFailedMessage = /Loading chunk [\d]+ failed/;

		if (chunkFailedMessage.test(error.message)) {
			window.location.reload();
			return;
		}
		//only send half of errors
		const unix = Math.floor(Date.now() / 1000);
		if (unix % 2 !== 0) {
			return;
		}
		console.error(error);
		Sentry.captureException(error.originalError || error.error || error);
		// Sentry.showReportDialog({ eventId });
	}
}

export function jwtOptionsFactory(
	authV1Service: AuthV1Service,
	store: Store,
	router: Router
) {
	return {
		tokenGetter: () => {
			const auth1CognitoDataObj = authV1Service.getAuthData();
			const authIdToken = store.selectSnapshot(StoredAuthState.authIdToken);

			if (
				auth1CognitoDataObj.IdToken &&
				auth1CognitoDataObj.IdToken !== authIdToken
			) {
				store.dispatch(new StoredAuthSetAction(auth1CognitoDataObj));
				return auth1CognitoDataObj.IdToken;
			}
			return authIdToken;
		},
		allowedDomains: environment.allowedDomains,
		disallowedRoutes: environment.disallowedRoutes,
	};
}
@NgModule({
	declarations: [AppComponent, NotFoundComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		HttpClientModule,
		JwtModule.forRoot({
			jwtOptionsProvider: {
				provide: JWT_OPTIONS,
				useFactory: jwtOptionsFactory,
				deps: [AuthV1Service, Store, Router],
			},
		}),
		NgxsModule.forRoot([StoredAuthState, PortalAuthState], ngxsConfig),
		NgxsStoragePluginModule.forRoot({
			key: [
				StoredAuthState,
				PortalAuthState,
				RequestDemoFormState,
				StoredPaginationState,
				CompanyRegisterFormState,
				SignInFormState,
				// InducteeRegisterFormState,
			],
		}),
		NgxsReduxDevtoolsPluginModule.forRoot(),
		NgxsLoggerPluginModule.forRoot(),
		NgxsRouterPluginModule.forRoot(),
		FroalaEditorModule.forRoot(),
		FroalaViewModule.forRoot(),
		NgxsFormPluginModule.forRoot(),
		NgxsEmitPluginModule.forRoot(),
		BrowserAnimationsModule,
		MatSnackBarModule,
	],
	providers: [
		CookieService,
		{ provide: ErrorHandler, useClass: SentryErrorHandler },
		{
			provide: NgbDateParserFormatter,
			useClass: LuxonDateFormatter,
		},
		{ provide: DateAdapter, useClass: AppDateAdapter },
		{ provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
